import { useMemo } from 'react';

import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState.ts';

import type { InitialDraft } from './fetchDraft.ts';
import { getDraftFromPromise } from './getDraft.ts';
import { COLLAB_EDIT_PROVIDER_MEASURES } from './CollabEditProviderMeasures.ts';

export const DRAFT_FETCH_TIMEOUT = 1000; // milliseconds
const TimedOut = Symbol('TimedOut');

export async function getPreloadedDraft(contentId: string, transitionId: number | null) {
	const initialLoad = !transitionId;
	const preload = window.__PRELOAD_DRAFT__;

	if (
		!initialLoad || // preloaded draft only makes sense for initial load
		!preload ||
		preload.contentId !== contentId
	) {
		return null;
	}

	CollabEditProviderMeasures.markMeasureStart(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_PRELOAD_NCS_DRAFT,
		preload.startTime,
	);

	const timeoutPromise = new Promise((resolve) =>
		setTimeout(resolve, DRAFT_FETCH_TIMEOUT, TimedOut),
	);

	// In happy path, this will resolve immediately, since the draft fetch initiated in preloader
	// However, if the draft fetch is still pending, the wait will be limited to DRAFT_FETCH_TIMEOUT
	const awaitResult = await Promise.race([
		getDraftFromPromise(contentId, preload.draftFetchPromise),
		timeoutPromise,
	]);

	CollabEditProviderMeasures.markMeasureEnd(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_PRELOAD_NCS_DRAFT,
	);

	if (awaitResult === TimedOut) {
		CollabEditProviderMeasures.markMeasureStart(
			COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_PRELOAD_NCS_DRAFT_TIMEOUT,
		);

		return null;
	}

	return awaitResult as InitialDraft | null;
}

export function usePreloadedDraft(contentId: string) {
	const transitionId = useTransitionId();

	const preloadedDraftPromise = useMemo(
		() => getPreloadedDraft(contentId, transitionId),
		[contentId, transitionId],
	);

	return { preloadedDraftPromise };
}
